import React from 'react';
import PropTypes from 'prop-types';
import { Card } from 'common/Card';
import formatDate from 'common/utils/Dates/formatDate';
import { useFeatureFlag } from 'common/hooks';
import { AUTHORIZATION_STATUSES } from 'common/Status/authorizationConstants';
import AssignUserToAuth from '../../components/AssignUserToAuth';
import MarkAuthorizationAsUrgentCheckbox from '../../components/MarkAuthorizationAsUrgentCheckbox';
import ServiceAuthorizationStatusIndicator from '../../components/ServiceAuthorizationStatusIndicator';
import AuthorizationSummarySection from './AuthorizationSummarySection';
import FeeScheduleProgramSection from './FeeScheduleProgramSection';
import { TwoPanelCardBody } from './Layout';

const AuthorizationDetailsCard = ({
  authorization,
  feeScheduleProgram,
  dateRequested,
  dateRequestedStartsAt,
  dateRequestedEndsAt,
  isSingleDateRequested,
  isFspLoaded,
  isCostBased,
  previousAuth,
  showOriginalValues,
}) => {
  const updateEditAuthorizationsFeatureEnabled = useFeatureFlag('pays-5650-update-edit-authorizations');
  const markAsUrgentEnabled = useFeatureFlag('pays-auth-mark-as-urgent');
  const isUpdateRequest = updateEditAuthorizationsFeatureEnabled && authorization.original_service_authorization;
  const isOpenStatus = authorization.state === AUTHORIZATION_STATUSES.open;

  const title = isUpdateRequest && isOpenStatus ?
    `Authorization Update Request #${authorization.short_id}` :
    `Authorization Request #${authorization.short_id}`;

  const showMarkAsUrgentCheckbox = markAsUrgentEnabled &&
    [AUTHORIZATION_STATUSES.open, AUTHORIZATION_STATUSES.inReview].includes(authorization.state);

  return (
    <Card className="bg-white overflow-visible" data-testid="auth-details-card">
      <div className="px-6 flex justify-between">
        <div className="py-4">
          <h1 className="text-text-blue font-heavy-font text-2xl leading-8">
            {title}
          </h1>
          <div className="flex space-x-6 items-center">
            <p className="text-text-blue text-sm leading-5">Last updated: {formatDate(authorization.updated_at)}</p>
            <ServiceAuthorizationStatusIndicator
              status={authorization.state}
              autoApproved={authorization.auto_approved}
              isUrgent={authorization.urgent}
              isUpdateRequest={isUpdateRequest}
              hideUrgentText={showMarkAsUrgentCheckbox}
            />
            {showMarkAsUrgentCheckbox && (
              <MarkAuthorizationAsUrgentCheckbox
                authorizationId={authorization.id}
                isUrgent={authorization.urgent}
              />
            )}
          </div>
        </div>
        <div className="py-4">
          <AssignUserToAuth
            authorizationId={authorization.id}
            currentAssigneeId={authorization.assignee?.id}
            state={authorization.state}
          />
        </div>
      </div>
      <div className="bg-section">
        <TwoPanelCardBody
          firstPanel={(
            <AuthorizationSummarySection
              authorization={authorization}
              feeScheduleProgram={feeScheduleProgram}
              dateRequested={dateRequested}
              dateRequestedStartsAt={dateRequestedStartsAt}
              dateRequestedEndsAt={dateRequestedEndsAt}
              isSingleDateRequested={isSingleDateRequested}
              isFspLoaded={isFspLoaded}
              previousAuth={previousAuth}
              showOriginalValues={showOriginalValues}
            />
          )}
          secondPanel={(
            <dl className="m-0 grid grid-cols-2 gap-x-8 gap-y-2">
              <FeeScheduleProgramSection
                feeScheduleProgram={feeScheduleProgram}
                isLoaded={isFspLoaded}
                isCostBased={isCostBased}
              />
            </dl>
          )}
        />
      </div>
    </Card>
  );
};

AuthorizationDetailsCard.propTypes = {
  authorization: PropTypes.shape({
    id: PropTypes.string.isRequired,
    short_id: PropTypes.string.isRequired,
    updated_at: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired,
    auto_approved: PropTypes.bool,
    requested_unit_amount: PropTypes.number,
    requested_cents: PropTypes.number,
    clinical_modifications: PropTypes.arrayOf(PropTypes.shape({
      code: PropTypes.string,
    })),
    requester: PropTypes.shape({
      full_name: PropTypes.string,
      provider: PropTypes.shape({
        name: PropTypes.string,
      }),
    }),
    assignee: PropTypes.shape({
      id: PropTypes.string,
    }),
    urgent: PropTypes.bool,
    original_service_authorization: PropTypes.object,
  }).isRequired,
  feeScheduleProgram: PropTypes.shape({
    unit: PropTypes.string,
    name: PropTypes.string,
    payment_type: PropTypes.string,
    unit_rate: PropTypes.number,
    cap_information: PropTypes.string,
    cm_code_required_on_authorization: PropTypes.bool,
  }).isRequired,
  dateRequested: PropTypes.string,
  dateRequestedStartsAt: PropTypes.string.isRequired,
  dateRequestedEndsAt: PropTypes.string.isRequired,
  isSingleDateRequested: PropTypes.bool.isRequired,
  isFspLoaded: PropTypes.bool.isRequired,
  isCostBased: PropTypes.bool.isRequired,
  previousAuth: PropTypes.shape({
    requested_unit_amount: PropTypes.number,
    requested_cents: PropTypes.number,
    requested_starts_at: PropTypes.string,
    requested_ends_at: PropTypes.string,
    clinical_modifications: PropTypes.arrayOf(PropTypes.shape({
      code: PropTypes.string,
    })),
  }),
  showOriginalValues: PropTypes.bool,
};

AuthorizationDetailsCard.defaultProps = {
  dateRequested: null,
  previousAuth: {},
  showOriginalValues: false,
};

export default AuthorizationDetailsCard;
